/**
 * D-044 S5 (new-tab model) — view-as client-state, PER-TAB ізоляція.
 *
 * Bearer-пара (sessionId+secret) + display-метадані живуть у **sessionStorage**
 * (НЕ localStorage!) — щоб НЕ текти в адмінську вкладку. Адмін-вкладка ніколи
 * не пише bearer; НОВА view-as вкладка seed'иться з URL-hash при відкритті
 * (`#kviewas=<sessionId>.<secret>|<expiresAt>|<targetName>`), переносить у свій
 * sessionStorage і чистить URL. Так swap (X-Kormax-View-As на кожен запит)
 * присутній ЛИШЕ у view-as вкладці; адмінська вкладка (без bearer) лишається
 * повноцінним адміном. raw targetUserId НІКОЛИ не зберігається/не їде.
 */

const KEY = "kormax-view-as";
const HASH_PREFIX = "kviewas=";

export interface ViewAsState {
  sessionId: string;
  secret: string;
  targetName: string;
  expiresAt: string; // server time 'YYYY-MM-DD HH:mm:ss'
}

function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function getViewAsState(): ViewAsState | null {
  try {
    const raw = store()?.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as ViewAsState;
    if (!v.sessionId || !v.secret) return null;
    return v;
  } catch {
    return null;
  }
}

export function setViewAsState(v: ViewAsState): void {
  store()?.setItem(KEY, JSON.stringify(v));
}

export function clearViewAsState(): void {
  store()?.removeItem(KEY);
}

export function viewAsHeaderValue(): string | null {
  const v = getViewAsState();
  return v ? `${v.sessionId}.${v.secret}` : null;
}

/** Контрольні endpoint'и view-as — БЕЗ swap-заголовка (виконуються від адміна). */
export function isViewAsControlPath(path: string): boolean {
  return path.startsWith("/Custom/admin-center/view-as/");
}

/**
 * Будує URL для НОВОЇ вкладки: bearer у hash-фрагменті (не залишає сліду в
 * history/referrer; читається лише цією вкладкою). Поля розділені '|'.
 */
export function buildViewAsTabUrl(s: ViewAsState): string {
  const payload = [s.sessionId, s.secret, s.expiresAt, s.targetName].join("|");
  return `${window.location.origin}/today#${HASH_PREFIX}${encodeURIComponent(payload)}`;
}

/**
 * Викликається ПЕРШИМ рядком у main.tsx (до render). Якщо в URL є view-as
 * payload — переносить у sessionStorage цієї вкладки і ЧИСТИТЬ hash. Повертає
 * true, якщо вкладка — view-as. (Адмінська вкладка не має hash → no-op.)
 */
export function seedViewAsFromHash(): boolean {
  try {
    const hash = window.location.hash || "";
    const idx = hash.indexOf(HASH_PREFIX);
    if (idx === -1) return false;

    const raw = decodeURIComponent(hash.slice(idx + HASH_PREFIX.length));
    const [sessionId, secret, expiresAt, targetName] = raw.split("|");
    if (!sessionId || !secret) return false;

    setViewAsState({ sessionId, secret, expiresAt: expiresAt ?? "", targetName: targetName ?? "" });

    // прибрати bearer з URL (не лишати в адресному рядку)
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", clean);
    return true;
  } catch {
    return false;
  }
}
